import { Document, Page, pdf, StyleSheet, Text, View } from '@react-pdf/renderer';
import React from 'react';

import type {
  MatchedPair,
  NotInRnicDecision,
  RnicNotInFileDecision,
} from './ImportDetail.module';
import type { ManualTreatmentPdfClassification, ManualTreatmentPdfEntry } from './manualTreatmentPdfClassification';
import {
  classifyManualTreatmentForPdf,
} from './manualTreatmentPdfClassification';

interface ManualTreatmentPdfMeta {
  importId?: number | string;
  companyName?: string;
  fileName?: string;
  createdAt?: string;
}

export interface ManualTreatmentPdfProps {
  classification: ManualTreatmentPdfClassification;
  meta?: ManualTreatmentPdfMeta;
  title?: string;
}

// Sanitize text to prevent unsupported characters that crash react-pdf
const sanitizeText = (text: string | undefined | null): string => {
  if (!text) {return '';}
  // Replace problematic characters and normalize
  return String(text)
    .replaceAll(/[\u0000-\u001F\u007F-\u009F]/g, '') // Remove control characters
    .replaceAll('�', '') // Remove replacement character
    .replaceAll(/[\u2028\u2029]/g, '\n') // Replace line/paragraph separators
    .trim();
};

// A4 dimensions in points (1 point = 1/72 inch)
const A4_SIZE = { width: 595.28, height: 841.89 } as const;

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    paddingTop: 40,
    paddingBottom: 50,
    paddingHorizontal: 40,
    fontFamily: 'Helvetica',
    fontSize: 10,
    color: '#1f2937',
    minHeight: '100%',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottomWidth: 1,
    borderBottomColor: '#d1d5db',
    paddingBottom: 12,
    marginBottom: 24,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: '#374151',
  },
  headerMeta: {
    fontSize: 8,
    color: '#6b7280',
    marginTop: 2,
  },
  logoPlaceholder: {
    width: 32,
    height: 32,
    marginRight: 10,
    backgroundColor: '#134094',
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  logoText: {
    color: '#ffffff',
    fontSize: 14,
    fontFamily: 'Helvetica-Bold',
  },
  coverPage: {
    paddingTop: 60,
    paddingBottom: 60,
    justifyContent: 'flex-start',
  },
  coverHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 2,
    borderBottomColor: '#134094',
    paddingBottom: 16,
    marginBottom: 0,
  },
  coverLogoPlaceholder: {
    width: 50,
    height: 50,
    marginRight: 16,
    backgroundColor: '#134094',
    borderRadius: 6,
    justifyContent: 'center',
    alignItems: 'center',
  },
  coverLogoText: {
    color: '#ffffff',
    fontSize: 22,
    fontFamily: 'Helvetica-Bold',
  },
  coverBrandName: {
    fontSize: 18,
    fontFamily: 'Helvetica-Bold',
    color: '#134094',
  },
  coverContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 120,
  },
  coverTitle: {
    fontSize: 22,
    fontFamily: 'Helvetica-Bold',
    color: '#0f172a',
    marginBottom: 16,
    textAlign: 'center',
    maxWidth: 450,
  },
  coverSubtitle: {
    fontSize: 14,
    color: '#374151',
    marginBottom: 8,
    textAlign: 'center',
  },
  coverMeta: {
    fontSize: 11,
    color: '#6b7280',
    marginTop: 24,
    textAlign: 'center',
  },
  coverFooter: {
    position: 'absolute',
    bottom: 60,
    left: 40,
    right: 40,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    paddingTop: 12,
  },
  coverFooterText: {
    fontSize: 8,
    color: '#9ca3af',
    textAlign: 'center',
  },
  sectionTitle: {
    fontSize: 14,
    fontFamily: 'Helvetica-Bold',
    color: '#0f172a',
    marginBottom: 6,
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#134094',
  },
  sectionSubtitle: {
    fontSize: 9,
    color: '#6b7280',
    marginBottom: 16,
    fontStyle: 'italic',
  },
  sectionDescriptionBox: {
    marginBottom: 16,
    padding: 10,
    backgroundColor: '#f8fafc',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 4,
  },
  sectionDescLabel: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    color: '#1e40af',
    marginBottom: 3,
    marginTop: 6,
  },
  sectionDescText: {
    fontSize: 8.5,
    color: '#374151',
    lineHeight: 1.5,
  },
  sectionDescBullet: {
    fontSize: 8.5,
    color: '#374151',
    paddingLeft: 10,
    marginBottom: 1,
  },
  entry: {
    marginBottom: 10,
    paddingBottom: 8,
    paddingLeft: 8,
    borderLeftWidth: 2,
    borderLeftColor: '#e5e7eb',
  },
  entryTitle: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: '#1f2937',
  },
  entryDetail: {
    fontSize: 9,
    color: '#4b5563',
    marginTop: 2,
  },
  noteList: {
    marginTop: 4,
    paddingLeft: 8,
  },
  entryNote: {
    fontSize: 8,
    color: '#0369a1',
    marginBottom: 1,
  },
  empty: {
    fontSize: 10,
    color: '#9ca3af',
    fontStyle: 'italic',
    paddingVertical: 20,
    textAlign: 'center',
  },
  pageNumber: {
    position: 'absolute',
    bottom: 20,
    right: 40,
    fontSize: 8,
    color: '#9ca3af',
  },
  // Cover letter styles
  letterBody: {
    fontSize: 10,
    color: '#1f2937',
    lineHeight: 1.6,
  },
  letterParagraph: {
    marginBottom: 10,
  },
  letterDynamic: {
    fontFamily: 'Helvetica-Bold',
    color: '#dc2626',
  },
  letterPointTitle: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    color: '#0284c7',
    marginTop: 14,
    marginBottom: 4,
  },
  letterSubHeading: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: '#1f2937',
    marginTop: 6,
    marginBottom: 2,
  },
  letterBullet: {
    fontSize: 10,
    color: '#1f2937',
    paddingLeft: 12,
    marginBottom: 2,
  },
  checkboxGroup: {
    marginTop: 6,
    paddingLeft: 4,
  },
  checkboxSectionLabel: {
    fontSize: 8.5,
    fontFamily: 'Helvetica-Bold',
    color: '#1f2937',
    marginTop: 4,
    marginBottom: 3,
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 3,
  },
  checkboxBox: {
    width: 10,
    height: 10,
    borderWidth: 1,
    borderColor: '#374151',
    borderRadius: 1,
    marginRight: 6,
  },
  checkboxLabel: {
    fontSize: 8.5,
    color: '#374151',
  },
  immatInputBox: {
    width: 120,
    height: 12,
    borderWidth: 1,
    borderColor: '#374151',
    borderRadius: 1,
    marginLeft: 4,
  },
  freeTextBox: {
    width: '100%',
    height: 42,
    borderWidth: 1,
    borderColor: '#374151',
    borderRadius: 1,
    marginTop: 2,
    marginLeft: 16,
  },
});

const formatEntryLine = (entry: ManualTreatmentPdfEntry): string => {
  const name = sanitizeText(entry.name) || 'Nom inconnu';
  const immat = sanitizeText(entry.immatriculation) || 'immat. manquante';
  return `${name} - ${immat}`;
};

const LogoPlaceholder: React.FC<{ size: 'small' | 'large' }> = ({ size }) => (
  <View style={size === 'large' ? styles.coverLogoPlaceholder : styles.logoPlaceholder}>
    <Text style={size === 'large' ? styles.coverLogoText : styles.logoText}>DJ</Text>
  </View>
);

const PageHeader: React.FC<{ docTitle: string; meta?: ManualTreatmentPdfMeta }> = ({ docTitle, meta }) => (
  <View style={styles.header} fixed>
    <View style={styles.headerLeft}>
      <LogoPlaceholder size="small" />
      <View>
        <Text style={styles.headerTitle}>{sanitizeText(docTitle)}</Text>
        {meta?.fileName && <Text style={styles.headerMeta}>Fichier : {sanitizeText(meta.fileName)}</Text>}
      </View>
    </View>
  </View>
);

const CheckboxRow: React.FC<{ label: string }> = ({ label }) => (
  <View style={styles.checkboxRow}>
    <View style={styles.checkboxBox} />
    <Text style={styles.checkboxLabel}>{sanitizeText(label)}</Text>
  </View>
);

const Section1And2Checkboxes: React.FC = () => (
  <View style={styles.checkboxGroup}>
    <Text style={styles.checkboxSectionLabel}>{'Avons nous bien trouvé la bonne copropriété dans le rapprochement que nous avons effectué ?'}</Text>
    <CheckboxRow label="Oui" />
    <View style={styles.checkboxRow}>
      <View style={styles.checkboxBox} />
      <Text style={styles.checkboxLabel}>{'Non, merci de détailler (immat et copro, ASL, AFUL, vous ne la gérez plus) :'}</Text>
    </View>
    <View style={styles.freeTextBox} />
  </View>
);

const Section3Checkboxes: React.FC = () => (
  <View style={styles.checkboxGroup}>
    <Text style={styles.checkboxSectionLabel}>{'Gérez-vous toujours cette copropriété ?'}</Text>
    <CheckboxRow label="Oui" />
    <CheckboxRow label="Non" />
    <Text style={styles.checkboxSectionLabel}>{"Si oui, veuillez nous indiquer s'il s'agit :"}</Text>
    <CheckboxRow label="Une ASL" />
    <CheckboxRow label="Une AFUL" />
    <CheckboxRow label="Un immeuble tertiaire" />
    <View style={styles.checkboxRow}>
      <View style={styles.checkboxBox} />
      <Text style={styles.checkboxLabel}>{"Une copropriété et l'immatriculation est :"}</Text>
      <View style={styles.immatInputBox} />
    </View>
  </View>
);

const Section2Checkboxes: React.FC = () => (
  <View style={styles.checkboxGroup}>
    <Text style={styles.checkboxSectionLabel}>{'Gérez-vous toujours cette copropriété ?'}</Text>
    <CheckboxRow label="Oui" />
    <CheckboxRow label="Non" />
  </View>
);

const CoverLetterPage: React.FC<{
  classification: ManualTreatmentPdfClassification;
  docTitle: string;
  meta?: ManualTreatmentPdfMeta;
}> = ({ classification, docTitle, meta }) => {
  const successCount = classification.success.length;
  const manualCount =
    classification.adjustments.length +
    classification.aslAfulTertiaire.length +
    classification.ignoredFile.length +
    classification.ignoredRnic.length +
    classification.forcedRnic.length;

  return (
    <Page size={A4_SIZE} style={styles.page} wrap>
      <PageHeader docTitle={docTitle} meta={meta} />

      <View style={styles.letterBody}>
        {/* Intro paragraph */}
        <View style={styles.letterParagraph}>
          <Text>
            Ce rapport d'importation des immeubles que vous gérez contient 6 points et l'analyse pour chaque immeuble de
            sa situation à ce jour dans le module CROISSANCE.
          </Text>
        </View>

        {/* Success summary */}
        <View style={styles.letterParagraph}>
          <Text>
            Pour <Text style={styles.letterDynamic}>{successCount}</Text> copropriétés, nous avons pu valider le n° RNIC
            avec les autres informations (nom, adresse). Pour celles-là vous n'avez rien à faire. Elles sont dans le
            point 6. Vous pouvez néanmoins les regarder afin d'être totalement sûr.
          </Text>
        </View>

        {/* Manual treatment summary */}
        <View style={styles.letterParagraph}>
          <Text>
            Pour <Text style={styles.letterDynamic}>{manualCount}</Text> copropriétés, il y a 5 points à valider en
            fonction du traitement et des résultats que nous avons obtenus. Vous trouverez pour chaque point, ce que nous
            avons fait et ce que vous devez vérifier.
          </Text>
        </View>

        {/* Point 1 – Entrées ignorées depuis le fichier */}
        <Text style={styles.letterPointTitle}>Point 1 : pour les immeubles présents dans ce point</Text>
        <Text style={styles.letterSubHeading}>A - Ce que nous avons fait :</Text>
        <View style={styles.letterParagraph}>
          <Text>
            Rien pour ces immeubles : il s'agit des entrées de votre fichier que nous n'avons pas su faire correspondre
            à une entrée du registre des copros ou que nous n'avons pas pu identifier comme ASL, AFUL, immeubles
            tertiaires.
          </Text>
        </View>
        <Text style={styles.letterSubHeading}>B - Ce que vous devez faire :</Text>
        <View style={styles.letterParagraph}>
          <Text>Nous indiquer pour chaque immeuble de ce point :</Text>
        </View>
        <Text style={styles.letterBullet}>- S'il s'agit d'une AFUL / ASL ou d'un immeuble tertiaire</Text>
        <Text style={styles.letterBullet}>
          - S'il s'agit d'une copropriété, nous donner le numéro d'immatriculation (si elle n'est pas enregistrée dans
          le registre : alors il faut l'enregistrer de votre côté)
        </Text>

        {/* Point 2 & 3 – Copropriétés ignorées / intégrées du RNIC */}
        <Text style={styles.letterPointTitle}>Point 2 et Point 3 : pour les copropriétés présentes dans ces points</Text>
        <Text style={styles.letterSubHeading}>A - Ce que nous avons fait :</Text>
        <View style={styles.letterParagraph}>
          <Text>
            Nous avions de notre côté les copropriétés présentes dans ces points comme étant des copropriétés gérées par
            vous. Mais nous ne les avons pas trouvées dans le fichier que vous nous avez fourni. Nous les avons divisées en
            2 points distincts car nous avons effectué des traitements différents :
          </Text>
        </View>
        <Text style={styles.letterBullet}>- Pour le point 2, nous avons ignoré ces copropriétés</Text>
        <Text style={styles.letterBullet}>
          - Pour le point 3, nous les avons intégrées dans votre fichier pour le moment (mais sans être totalement sûr)
        </Text>
        <Text style={styles.letterSubHeading}>B - Ce que vous devez faire pour ces 2 points :</Text>
        <View style={styles.letterParagraph}>
          <Text>Valider qu'il s'agit bien de copropriétés :</Text>
        </View>
        <Text style={styles.letterBullet}>- Que vous avez perdues</Text>
        <Text style={styles.letterBullet}>- Que vous gérez encore</Text>

        {/* Point 4 – Ajustements effectués */}
        <Text style={styles.letterPointTitle}>Point 4 : pour les copropriétés présentes dans ce point</Text>
        <Text style={styles.letterSubHeading}>A - Ce que nous avons fait :</Text>
        <View style={styles.letterParagraph}>
          <Text>
            Nous avons effectué un rapprochement manuel via le nom, ou bien l'adresse de la copropriété car, soit il n'y avait pas d'immatriculation dans votre fichier, soit elle était différente de celle du RNIC.
          </Text>
        </View>
        <Text style={styles.letterSubHeading}>B - Ce que vous devez faire :</Text>
        <View style={styles.letterParagraph}>
          <Text>La correspondance a-t-elle été faite correctement ?</Text>
        </View>
        <View style={styles.letterParagraph}>
          <Text>L'immatriculation correspond-elle à celle dans votre logiciel ?</Text>
        </View>
        <View style={styles.letterParagraph}>
          <Text>
            Si vous détectez une anomalie, merci de nous indiquer le nom de la copropriété concernée et ce qu'il faut
            changer pour celle-ci.
          </Text>
        </View>

        {/* Point 5 – ASL/AFUL/Tertiaire */}
        <Text style={styles.letterPointTitle}>Point 5 : pour les immeubles présents dans ce point</Text>
        <Text style={styles.letterSubHeading}>A - Ce que nous avons fait :</Text>
        <View style={styles.letterParagraph}>
          <Text>Nous avons identifié des immeubles et leur avons attribué un des statuts suivants :</Text>
        </View>
        <Text style={styles.letterBullet}>- ASL</Text>
        <Text style={styles.letterBullet}>- AFUL</Text>
        <Text style={styles.letterBullet}>- Immeuble tertiaire</Text>
        <Text style={styles.letterSubHeading}>B - Ce que vous devez faire :</Text>
        <View style={styles.letterParagraph}>
          <Text>Vérifier que nous avons bien attribué le bon statut ASL / AFUL / immeubles tertiaires.</Text>
        </View>
        <View style={styles.letterParagraph}>
          <Text>
            Si vous détectez une anomalie dans l'attribution des immeubles listés, merci de nous indiquer le nom de
            l'immeuble concerné et ce qu'il faut changer pour celui-ci.
          </Text>
        </View>
        <View style={styles.letterParagraph}>
          <Text>
            Si vous avez d'autres ASL, AFUL, immeubles tertiaires non présents dans ce point 5 merci de nous les
            indiquer également pour que nous les rajoutions.
          </Text>
        </View>

        {/* Point 6 – Imports sans ajustement */}
        <Text style={styles.letterPointTitle}>Point 6 : pour les copropriétés présentes dans ce point</Text>
        <View style={styles.letterParagraph}>
          <Text>
            Il s'agit des entrées pour lesquelles l'import s'est déroulé sans encombre, après une correspondance des
            noms et des numéros d'immatriculation effectuée. Une vérification est souhaitable.
          </Text>
        </View>
      </View>

      <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
    </Page>
  );
};

const renderList = (
  entries: ManualTreatmentPdfEntry[],
  emptyFallback: string,
  extraNote?: string,
  renderEntryExtras?: (entry: ManualTreatmentPdfEntry) => React.ReactElement | null,
): React.ReactElement => (
  <View>
    {extraNote ? <Text style={styles.sectionSubtitle}>{sanitizeText(extraNote)}</Text> : null}
    {entries.length === 0 ? (
      <Text style={styles.empty}>{sanitizeText(emptyFallback)}</Text>
    ) : (
      entries.map((entry, index) => (
        <View key={entry.key || `entry-${index}`} style={styles.entry} wrap={false}>
          <Text style={styles.entryTitle}>{formatEntryLine(entry)}</Text>
          {entry.address && <Text style={styles.entryDetail}>Adresse fichier : {sanitizeText(entry.address)}</Text>}
          {entry.matchedWith && (
            <Text style={styles.entryDetail}>
              Apparié RNIC : {sanitizeText(entry.matchedWith.name)} - {sanitizeText(entry.matchedWith.immatriculation)}
              {entry.matchedWith.address ? ` (${sanitizeText(entry.matchedWith.address)})` : ''}
            </Text>
          )}
          {(entry.note || entry.typeLabel || entry.source) && (
            <View style={styles.noteList}>
              {entry.note && <Text style={styles.entryNote}>- {sanitizeText(entry.note)}</Text>}
              {entry.typeLabel && <Text style={styles.entryNote}>- Type : {sanitizeText(entry.typeLabel)}</Text>}
              {entry.source === 'rnic' && <Text style={styles.entryNote}>- Entrée RNIC absente du fichier import</Text>}
              {entry.source === 'file' && <Text style={styles.entryNote}>- Entrée issue du fichier import</Text>}
            </View>
          )}
          {renderEntryExtras?.(entry) ?? null}
        </View>
      ))
    )}
  </View>
);

export const ManualTreatmentPdf: React.FC<ManualTreatmentPdfProps> = ({
  classification,
  meta,
  title = 'Synthèse de traitement manuel des copropriétés',
}) => {
  const docTitle = sanitizeText(title);
  const generatedAt = meta?.createdAt
    ? new Date(meta.createdAt).toLocaleString('fr-FR')
    : new Date().toLocaleString('fr-FR');

  // Summary counts for cover page
  const totalEntries =
    classification.adjustments.length +
    classification.aslAfulTertiaire.length +
    // classification.ignoredFile.length +
    // classification.ignoredRnic.length +
    classification.forcedRnic.length +
    classification.success.length;

  return (
    <Document title={docTitle} author="DÉCLARATIONS JURIDIQUES" subject="Synthèse traitement manuel import copro">
      {/* Cover Page */}
      <Page size={A4_SIZE} style={[styles.page, styles.coverPage]}>
        <View style={styles.coverHeader}>
          <LogoPlaceholder size="large" />
          <Text style={styles.coverBrandName}>DÉCLARATIONS JURIDIQUES</Text>
        </View>
        <View style={styles.coverContent}>
          <Text style={styles.coverTitle}>{docTitle}</Text>
          {meta?.companyName && <Text style={styles.coverSubtitle}>{sanitizeText(meta.companyName)}</Text>}
          {meta?.fileName && <Text style={styles.coverSubtitle}>Fichier : {sanitizeText(meta.fileName)}</Text>}
          <Text style={styles.coverMeta}>
            {`Généré le : ${generatedAt}\n${totalEntries} copropriété${totalEntries > 1 ? 's' : ''} traitée${totalEntries > 1 ? 's' : ''}`}
          </Text>
        </View>
        <View style={styles.coverFooter}>
          <Text style={styles.coverFooterText}>
            Document généré automatiquement - Traitement manuel des imports de copropriétés
          </Text>
        </View>
      </Page>

      {/* Cover Letter Page */}
      <CoverLetterPage classification={classification} docTitle={docTitle} meta={meta} />

      {/* 1. Ignored File Entries Page (was 3) */}
      <Page size={A4_SIZE} style={styles.page} wrap>
        <PageHeader docTitle={docTitle} meta={meta} />
        <Text style={styles.sectionTitle}>1. Entrées ignorées depuis le fichier ({classification.ignoredFile.length})</Text>
        <View style={styles.sectionDescriptionBox}>
          <Text style={styles.sectionDescLabel}>A - Ce que nous avons fait :</Text>
          <Text style={styles.sectionDescText}>
            Rien pour ces immeubles : il s'agit des entrées de votre fichier que nous n'avons pas su faire correspondre à une entrée du registre des copros ou que nous n'avons pas pu identifier comme ASL, AFUL, immeubles tertiaires.
          </Text>
          <Text style={styles.sectionDescLabel}>B - Ce que vous devez faire :</Text>
          <Text style={styles.sectionDescText}>Nous indiquer pour chaque immeuble de ce point :</Text>
          <Text style={styles.sectionDescBullet}>- S'il s'agit d'une AFUL / ASL ou d'un immeuble tertiaire</Text>
          <Text style={styles.sectionDescBullet}>
            - S'il s'agit d'une copropriété, nous donner le numéro d'immatriculation (si elle n'est pas enregistrée dans le registre : alors il faut l'enregistrer de votre côté)
          </Text>
        </View>
        {renderList(
          classification.ignoredFile,
          'Aucune entrée du fichier marquée comme ignorée.',
          undefined,
          () => <Section3Checkboxes />,
        )}
        <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
      </Page>

      {/* 2. Ignored RNIC Page (was 4) */}
      <Page size={A4_SIZE} style={styles.page} wrap>
        <PageHeader docTitle={docTitle} meta={meta} />
        <Text style={styles.sectionTitle}>2. Copropriétés ignorées du RNIC ({classification.ignoredRnic.length})</Text>
        <View style={styles.sectionDescriptionBox}>
          <Text style={styles.sectionDescLabel}>A - Ce que nous avons fait :</Text>
          <Text style={styles.sectionDescText}>
            Nous avions de notre côté les copropriétés présentes dans ce point comme étant des copropriétés gérées par vous. Mais nous ne les avons pas trouvées dans le fichier que vous nous avez fourni. Nous avons ignoré ces copropriétés.
          </Text>
          <Text style={styles.sectionDescLabel}>B - Ce que vous devez faire :</Text>
          <Text style={styles.sectionDescText}>Valider qu'il s'agit bien de copropriétés :</Text>
          <Text style={styles.sectionDescBullet}>- Que vous avez perdues</Text>
          <Text style={styles.sectionDescBullet}>- Que vous gérez encore</Text>
        </View>
        {renderList(
          classification.ignoredRnic,
          'Aucune copropriété du registre ignorée.',
          undefined,
          () => <Section2Checkboxes />,
        )}
        <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
      </Page>

      {/* 3. Forced RNIC Page (was 5) */}
      <Page size={A4_SIZE} style={styles.page} wrap>
        <PageHeader docTitle={docTitle} meta={meta} />
        <Text style={styles.sectionTitle}>3. Copropriétés intégrées depuis le RNIC ({classification.forcedRnic.length})</Text>
        <View style={styles.sectionDescriptionBox}>
          <Text style={styles.sectionDescLabel}>A - Ce que nous avons fait :</Text>
          <Text style={styles.sectionDescText}>
            Nous avions de notre côté les copropriétés présentes dans ce point comme étant des copropriétés gérées par vous. Mais nous ne les avons pas trouvées dans le fichier que vous nous avez fourni. Nous les avons intégrées dans votre fichier pour le moment (mais sans être totalement sûr).
          </Text>
          <Text style={styles.sectionDescLabel}>B - Ce que vous devez faire :</Text>
          <Text style={styles.sectionDescText}>Valider qu'il s'agit bien de copropriétés :</Text>
          <Text style={styles.sectionDescBullet}>- Que vous avez perdues</Text>
          <Text style={styles.sectionDescBullet}>- Que vous gérez encore</Text>
        </View>
        {renderList(
          classification.forcedRnic,
          'Aucune copropriété RNIC intégrée par forçage.',
          undefined,
          () => <Section2Checkboxes />,
        )}
        <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
      </Page>

      {/* 4. Adjustments Page (was 1) */}
      <Page size={A4_SIZE} style={styles.page} wrap>
        <PageHeader docTitle={docTitle} meta={meta} />
        <Text style={styles.sectionTitle}>4. Ajustements effectués ({classification.adjustments.length})</Text>
        <View style={styles.sectionDescriptionBox}>
          <Text style={styles.sectionDescLabel}>A - Ce que nous avons fait :</Text>
          <Text style={styles.sectionDescText}>
            Nous avons effectué un rapprochement manuel via le nom, ou bien l'adresse de la copropriété car, soit il n'y avait pas d'immatriculation dans votre fichier, soit elle était différente de celle du RNIC.
          </Text>
          <Text style={styles.sectionDescLabel}>B - Ce que vous devez faire :</Text>
          <Text style={styles.sectionDescText}>La correspondance a-t-elle été faite correctement ?</Text>
          <Text style={styles.sectionDescText}>L'immatriculation correspond-elle à celle dans votre logiciel ?</Text>
          <Text style={styles.sectionDescText}>
            Si vous détectez une anomalie, merci de nous indiquer le nom de la copropriété concernée et ce qu'il faut changer pour celle-ci.
          </Text>
        </View>
        {renderList(
          classification.adjustments,
          'Aucun ajustement manuel enregistré.',
          undefined,
          () => <Section1And2Checkboxes />,
        )}
        <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
      </Page>

      {/* 5. ASL/AFUL/Tertiaire Page (was 2) */}
      <Page size={A4_SIZE} style={styles.page} wrap>
        <PageHeader docTitle={docTitle} meta={meta} />
        <Text style={styles.sectionTitle}>5. Entrées typées ASL / AFUL / Tertiaire ({classification.aslAfulTertiaire.length})</Text>
        <View style={styles.sectionDescriptionBox}>
          <Text style={styles.sectionDescLabel}>A - Ce que nous avons fait :</Text>
          <Text style={styles.sectionDescText}>Nous avons identifié des immeubles et leur avons attribué un des statuts suivants :</Text>
          <Text style={styles.sectionDescBullet}>- ASL</Text>
          <Text style={styles.sectionDescBullet}>- AFUL</Text>
          <Text style={styles.sectionDescBullet}>- Immeuble tertiaire</Text>
          <Text style={styles.sectionDescLabel}>B - Ce que vous devez faire :</Text>
          <Text style={styles.sectionDescText}>Vérifier que nous avons bien attribué le bon statut ASL / AFUL / immeubles tertiaires.</Text>
          <Text style={styles.sectionDescText}>
            Si vous détectez une anomalie dans l'attribution des immeubles listés, merci de nous indiquer le nom de l'immeuble concerné et ce qu'il faut changer pour celui-ci.
          </Text>
          <Text style={styles.sectionDescText}>
            Si vous avez d'autres ASL, AFUL, immeubles tertiaires non présents dans ce point 5 merci de nous les indiquer également pour que nous les rajoutions.
          </Text>
        </View>
        {renderList(
          classification.aslAfulTertiaire,
          'Aucune entrée typée ASL/AFUL/Tertiaire.',
          undefined,
          () => <Section1And2Checkboxes />,
        )}
        <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
      </Page>

      {/* 6. Success Page (unchanged) */}
      <Page size={A4_SIZE} style={styles.page} wrap>
        <PageHeader docTitle={docTitle} meta={meta} />
        <Text style={styles.sectionTitle}>6. Imports sans ajustement ({classification.success.length})</Text>
        <View style={styles.sectionDescriptionBox}>
          <Text style={styles.sectionDescText}>
            Il s'agit des entrées pour lesquelles l'import s'est déroulé sans encombre, après une correspondance des noms et des numéros d'immatriculation effectuée. Une vérification est souhaitable.
          </Text>
        </View>
        {renderList(
          classification.success,
          'Aucune entrée validée automatiquement.',
        )}
        <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
      </Page>
    </Document>
  );
};

interface DownloadParams extends ClassificationParams {
  meta?: ManualTreatmentPdfMeta;
  title?: string;
  filename?: string;
}

interface ClassificationParams {
  matchedPairs: MatchedPair[];
  notInRnicDecisions: NotInRnicDecision[];
  rnicNotInFileDecisions: RnicNotInFileDecision[];
  fileAddressMap?: Map<string, string>;
  rnicAddressMap?: Map<string, string>;
  importSuccessCopros?: {
    immatriculation: string;
    name: string;
    rnicName?: string;
    rnicAddress?: string;
  }[];
}

export async function generateManualTreatmentPdfBlob(props: DownloadParams): Promise<Blob> {
  const classification = classifyManualTreatmentForPdf(props);
  const doc = <ManualTreatmentPdf classification={classification} meta={props.meta} title={props.title} />;
  return pdf(doc).toBlob();
}

export async function downloadManualTreatmentPdf(props: DownloadParams): Promise<void> {
  const classification = classifyManualTreatmentForPdf(props);
  if (classification.errors.length > 0) {
    throw new Error(classification.errors.join('\n'));
  }

  const doc = <ManualTreatmentPdf classification={classification} meta={props.meta} title={props.title} />;
  const blob = await pdf(doc).toBlob();
  const pdfBlob = new Blob([blob], { type: 'application/pdf' });

  const today = new Date().toISOString().split('T')[0];
  let finalFilename = props.filename || `synthese_import_copro_${today}.pdf`;
  if (!finalFilename.toLowerCase().endsWith('.pdf')) {
    finalFilename += '.pdf';
  }

  const url = URL.createObjectURL(pdfBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = finalFilename;
  link.type = 'application/pdf';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
